/** Cantidad kardex: 3 decimales (igual que backend ReportController::q3). */
export const q3 = (value) => Math.round(Number(value || 0) * 1000) / 1000;

/** Monto kardex: 4 decimales (igual que backend ReportController::q4). */
export const q4 = (value) => Math.round(Number(value || 0) * 10000) / 10000;

/** Costo promedio a partir de existencias acumuladas. */
export const kardexAverageCost = (stock, totalValue) => {
  const s = q3(stock);
  if (Math.abs(s) <= TOLERANCE_ZERO) return 0;
  return q4(totalValue / s);
};

/** Costo promedio en saldos tras un movimiento (igual que ReportController). */
export const resolveKardexAverageCostAfter = (movement, stock, totalValue) => {
  if (movement?.kardex_event === 'ajuste_costo' && movement?.average_cost_after != null) {
    const dbCost = q4(movement.average_cost_after);
    if (dbCost > 0) return dbCost;
  }
  return Math.abs(q3(stock)) > TOLERANCE_ZERO ? q4(totalValue / stock) : 0;
};

/** Valor total en saldos tras ajuste de costo. */
export const syncKardexTotalValueAfterCostAdjustment = (movement, stock, averageCostAfter, totalValue) => {
  if (
    movement?.kardex_event === 'ajuste_costo'
    && averageCostAfter > 0
    && Math.abs(q3(stock)) > TOLERANCE_ZERO
  ) {
    return q4(averageCostAfter * q3(stock));
  }
  return totalValue;
};

/** Al vaciar stock: costo promedio y total exacto pendiente (misma regla que backend). */
export function kardexSalidaTotalsWhenZeroingStock(
  stockBefore,
  totalValueBefore,
  quantity,
  averageCostBefore,
  lastAverageCost = 0
) {
  const qty = q3(quantity);
  const stockAfter = q3(stockBefore - qty);
  if (Math.abs(stockAfter) > TOLERANCE_ZERO) return null;

  const exitUnit = kardexSalidaUnitPrice(averageCostBefore, lastAverageCost);
  if (exitUnit <= 0) return null;

  const lineTotal = kardexLineTotal(quantity, exitUnit);
  if (Math.abs(q4(totalValueBefore) - lineTotal) <= TOLERANCE_ZERO) return null;

  return { unit_price: exitUnit, total: q4(totalValueBefore) };
}

/** Costo unitario de salida desde saldos (no el precio histórico de compra). */
export const kardexSalidaUnitPrice = (averageCostBefore, lastAverageCost = 0) => {
  const basis = Math.abs(averageCostBefore) > TOLERANCE_ZERO ? averageCostBefore : lastAverageCost;
  return basis > 0 ? kardexExitUnitPrice(basis) : 0;
};

/** Costo unitario de salida: promedio anterior redondeado a 4 decimales. */
export const kardexExitUnitPrice = (averageCostBefore) => q4(averageCostBefore);

/** Total de línea: cantidad × costo unitario a 4 decimales. */
export const kardexLineTotal = (quantity, unitPrice) => q4(q3(quantity) * q4(unitPrice));

/**
 * Total coherente a 4 decimales; tolera polvo de redondeo menor a S/ 0.05 en la línea.
 */
export function isKardexLineTotalConsistent(quantity, unitPrice, total) {
  const qty = q3(quantity);
  const unit = q4(unitPrice);
  const lineTotal = q4(total);
  if (qty <= 0) return true;
  if (q4(lineTotal / qty) === unit) return true;
  if (q4(kardexLineTotal(qty, unit)) === lineTotal) return true;
  return Math.abs(lineTotal - q4(kardexLineTotal(qty, unit))) < LINE_TOTAL_DUST_TOLERANCE;
}

const TOLERANCE_ZERO = 0.00009;

/** Residuo monetario tolerable cuando el stock ya está en cero (polvo de redondeo). */
export const ZERO_STOCK_DUST_TOLERANCE = 0.05;

/** Diferencia cantidad×costo vs total tolerable en una línea (polvo de redondeo en compras). */
export const LINE_TOTAL_DUST_TOLERANCE = 0.01;

/**
 * Normaliza la configuración «contar inconsistencias desde» del API o module settings.
 */
export function normalizeKardexInconsistencyCountFrom(raw) {
  if (!raw?.enabled || !raw?.date) return null;
  let time = raw.time ? String(raw.time) : '00:00:00';
  if (time.length === 5) time = `${time}:00`;
  return { enabled: true, date: String(raw.date).slice(0, 10), time };
}

/**
 * Indica si el movimiento cae dentro del periodo en el que se deben mostrar inconsistencias.
 */
export function movementIsOnOrAfterInconsistencyCutoff(movement, countFrom) {
  if (!countFrom?.enabled || !countFrom?.date) return true;
  const cutoffTime = countFrom.time || '00:00:00';
  const cutoff = new Date(`${countFrom.date}T${cutoffTime.length === 5 ? `${cutoffTime}:00` : cutoffTime}`);
  const movDate = String(movement?.movement_date ?? '').slice(0, 10);
  const movTimeRaw = movement?.movement_time ? String(movement.movement_time) : '00:00:00';
  const movTime = movTimeRaw.length === 5 ? `${movTimeRaw}:00` : movTimeRaw;
  const mov = new Date(`${movDate}T${movTime}`);
  if (Number.isNaN(cutoff.getTime()) || Number.isNaN(mov.getTime())) return true;
  return mov >= cutoff;
}

/**
 * Stock en cero con valor residual menor a S/ 0.05 (positivo o negativo).
 */
export function isZeroStockValueDust(stock, totalValue) {
  if (Math.abs(q3(stock)) > TOLERANCE_ZERO) return false;
  return Math.abs(Number(totalValue || 0)) < ZERO_STOCK_DUST_TOLERANCE;
}

/**
 * Detecta inconsistencias usando los mismos criterios que el backend.
 * Preferir average_cost_before / stock_after del API cuando existan.
 */
export function detectKardexInconsistencies(movement, { product, stockBefore, countFrom } = {}) {
  if (!movementIsOnOrAfterInconsistencyCutoff(movement, countFrom)) {
    return [];
  }

  const inconsistencies = [];
  const stockBeforeVal = stockBefore ?? movement.stock_before ?? 0;

  if (movement.cost_mismatch_alert?.has_mismatch) {
    inconsistencies.push({
      type: 'costo_recepcion',
      message:
        movement.cost_mismatch_alert.message ||
        `El costo unitario de recepción (S/ ${q4(movement.cost_mismatch_alert.reception_unit_price).toFixed(4)}) no coincide con el costo de la guía de traspaso ${movement.cost_mismatch_alert.transfer_guide_code} (S/ ${q4(movement.cost_mismatch_alert.transfer_unit_price).toFixed(4)})`,
    });
  }

  if (movement.movement_type === 'salida' && stockBeforeVal > 0) {
    const expectedUnit = q4(movement.average_cost_before ?? 0);
    const actualUnit = q4(movement.unit_price);
    if (actualUnit !== expectedUnit) {
      inconsistencies.push({
        type: 'costo_salida',
        message: `Costo unitario (S/ ${actualUnit.toFixed(4)}) no coincide con costo promedio anterior (S/ ${expectedUnit.toFixed(4)})`,
      });
    }
  }

  const expectedTotal = kardexLineTotal(movement.quantity, movement.unit_price);
  if (!isKardexLineTotalConsistent(movement.quantity, movement.unit_price, movement.total)) {
    inconsistencies.push({
      type: 'total_inconsistente',
      message: `Total inconsistente: S/ ${q4(movement.total).toFixed(4)} ≠ cantidad × costo (S/ ${expectedTotal.toFixed(4)})`,
    });
  }

  const stockAfter = movement.stock_after ?? movement.stockAfter;
  const totalValueAfter = movement.total_value_after ?? movement.totalValueAfter;

  if (stockAfter != null && stockAfter < -TOLERANCE_ZERO) {
    inconsistencies.push({
      type: 'stock_negativo',
      message: `Stock negativo: ${q3(stockAfter).toFixed(3)} ${product?.unit_of_measure || ''}`,
    });
  }

  if (
    totalValueAfter != null
    && totalValueAfter < -TOLERANCE_ZERO
    && !isZeroStockValueDust(stockAfter, totalValueAfter)
  ) {
    inconsistencies.push({
      type: 'valor_negativo',
      message: `Valor total negativo: S/ ${q4(totalValueAfter).toFixed(4)}`,
    });
  }

  return inconsistencies;
}

/**
 * Bloquea recálculo desde el modal solo cuando el costo viene de un documento
 * (factura/boleta) sin guía asociada. Las recepciones de traspaso sí se pueden recalcular.
 */
export function blocksKardexRecalculateFromModal(movement, inconsistencies = []) {
  if (!movement) return true;

  const hasCostIssue = inconsistencies.some(
    (inc) => inc.type === 'costo_salida' || inc.type === 'costo_recepcion'
  );
  if (!hasCostIssue) return false;

  if (movement.entry_guide_item_id) return false;
  if (movement.entry_guide_code) return false;
  if (movement.cost_mismatch_alert?.has_mismatch) return false;

  if (movement.document?.code || movement.document?.series) return true;

  return false;
}

/**
 * Recorre movimientos aplicando q3/q4 como el backend (para recálculo en pantalla).
 */
export function foldKardexMovements(movements, initialStock = 0, initialTotalValue = 0, adjustMovement) {
  let stock = q3(initialStock);
  let totalValue = q4(initialTotalValue);
  let lastAverageCost = kardexAverageCost(stock, totalValue);
  const result = [];

  for (const movement of movements || []) {
    const stockBefore = stock;
    const totalValueBefore = totalValue;
    const lastAverageCostBefore = lastAverageCost;
    const averageCostBefore = kardexAverageCost(stockBefore, totalValueBefore);
    if (averageCostBefore > 0) {
      lastAverageCost = averageCostBefore;
    }

    let unitPrice = movement.unit_price;
    let total = movement.total;

    if (typeof adjustMovement === 'function') {
      const adjusted = adjustMovement(movement, {
        stockBefore,
        totalValueBefore,
        averageCostBefore,
        lastAverageCost: lastAverageCostBefore,
      });
      if (adjusted) {
        unitPrice = adjusted.unit_price ?? unitPrice;
        total = adjusted.total ?? total;
      }
    }

    if (movement.movement_type === 'salida') {
      stock = q3(stock - movement.quantity);
      totalValue = q4(totalValue - total);
    } else {
      stock = q3(stock + movement.quantity);
      totalValue = q4(totalValue + total);
    }

    const averageCostAfter = resolveKardexAverageCostAfter(movement, stock, totalValue);
    totalValue = syncKardexTotalValueAfterCostAdjustment(movement, stock, averageCostAfter, totalValue);
    if (averageCostAfter > 0) {
      lastAverageCost = averageCostAfter;
    }

    result.push({
      ...movement,
      unit_price: unitPrice,
      total: q4(total),
      stock_before: stockBefore,
      stock_after: stock,
      total_value_before: q4(totalValueBefore),
      total_value_after: q4(totalValue),
      average_cost_before: averageCostBefore,
      average_cost_after: averageCostAfter,
    });
  }

  return result;
}
